import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'react-feather';

import env from '../env';
import {
  useVersionCopy,
  VersionCopyButton,
  VersionCopyNotice,
  VersionIconLink,
} from './VersionActions';

const Badge = styled.div`
  position: fixed;
  bottom: 6px;
  right: 8px;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 7px;
  max-width: calc(100vw - 16px);
  padding: 4px 6px 4px 10px;
  border-radius: 7px;
  background: transparent;
  color: ${({ theme }) => theme.box?.color ?? '#243e4b'};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  transition: background 180ms, color 180ms;
  > span,
  > button,
  > a {
    opacity: 0.4;
    transition: opacity 180ms;
  }
  > span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  > button,
  > a {
    width: 26px;
    height: 26px;
  }
  &:hover,
  &:focus-within {
    background: rgba(35, 44, 49, 0.88);
    color: #fff;
    > span,
    > button,
    > a {
      opacity: 1;
    }
  }
  @media (hover: none) {
    background: rgba(35, 44, 49, 0.78);
    color: #fff;
    > span,
    > button,
    > a {
      opacity: 1;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    > span,
    > button,
    > a {
      transition: none;
    }
  }
`;
interface FrontendVersionInfo {
  service: string;
  productVersion: string;
  semver: string;
  fullSemVer: string;
  shortSha: string;
  buildId: string;
  buildDate: string;
  sourceBranch: string;
  repository: string;
  releaseId: string;
}

export const VersionInfo: React.FC<{ collapse?: boolean }> = ({ collapse = true }) => {
  const [info, setInfo] = useState<FrontendVersionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { copy, feedback, sequence } = useVersionCopy();

  useEffect(() => {
    try {
      setInfo({
        service: 'webapp',
        productVersion: env.FUSION_OF_APP_VERSION,
        semver: env.FUSION_OF_SEMVER,
        fullSemVer: env.FUSION_OF_FULLSEMVER,
        shortSha: env.FUSION_OF_SHORTSHA,
        buildId: env.FUSION_OF_BUILD_ID,
        buildDate: env.FUSION_OF_BUILD_DATE,
        sourceBranch: env.FUSION_OF_SOURCE_BRANCH,
        repository: env.FUSION_OF_REPO_NAME,
        releaseId: env.FUSION_OF_RELEASE_ID,
      });
    } catch (e) {
      setError('failed to read version info');
      return;
    }
  }, []);

  if (error) {
    return (
      <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>version: error loading ({error})</div>
    );
  }
  if (!info) {
    return <div style={{ fontSize: '0.75rem', opacity: 0.5 }}>version: loading…</div>;
  }

  const body = (
    <table style={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
      <tbody>
        <tr>
          <th style={{ textAlign: 'left', paddingRight: 4 }}>release</th>
          <td style={{ overflowWrap: 'anywhere' }}>{info.releaseId || 'Unavailable'}</td>
        </tr>
        <tr>
          <th style={{ textAlign: 'left', paddingRight: 4 }}>semver</th>
          <td>{info.semver}</td>
        </tr>
        <tr>
          <th style={{ textAlign: 'left', paddingRight: 4 }}>commit</th>
          <td>{info.shortSha}</td>
        </tr>
        <tr>
          <th style={{ textAlign: 'left', paddingRight: 4 }}>build</th>
          <td>{info.buildId}</td>
        </tr>
        <tr>
          <th style={{ textAlign: 'left', paddingRight: 4 }}>date</th>
          <td>{info.buildDate}</td>
        </tr>
        <tr>
          <th style={{ textAlign: 'left', paddingRight: 4 }}>branch</th>
          <td>{info.sourceBranch}</td>
        </tr>
      </tbody>
    </table>
  );

  if (!collapse) return body;

  return (
    <Badge>
      <span>
        Build {info.productVersion} ({info.shortSha})
      </span>
      <VersionCopyButton
        label="current Web release identity"
        value={info.releaseId || info.fullSemVer}
        copy={(value) => copy(value, 'Release identity')}
      />
      <VersionIconLink
        to="/versions"
        aria-label="Open Versions and history"
        title="Open Versions and history"
      >
        <ExternalLink aria-hidden="true" />
      </VersionIconLink>
      <VersionCopyNotice role="status" aria-live="polite" aria-atomic="true">
        {feedback && <span key={sequence}>{feedback}</span>}
      </VersionCopyNotice>
    </Badge>
  );
};

export default VersionInfo;
